import {
  DEFAULT_KIOSK_POLICY_FLAGS,
  type KioskPolicyFlags,
  type KioskPolicyView,
} from "@ascleo/frontdesk-contracts/kiosk-policy/policy";
import type { KioskPolicyReadOutcome } from "../rpc/kiosk-policy.client";
import type { KioskEmergencyCacheSnapshot } from "./kiosk-emergency-cache";

/**
 * The source of the capability projection currently shown by the kiosk.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * const source: KioskPolicySource = "offline-cache";
 * ```
 */
export type KioskPolicySource =
  | "loading"
  | "current"
  | "offline-cache"
  | "unavailable";

/**
 * The source-aware policy projection consumed by patient-facing flows.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * declare const state: KioskPolicyState;
 *
 * if (state.source === "current" && state.patientCreationEnabled) {
 *   // Compose the current, server-authorized creation route.
 * }
 * ```
 */
export interface KioskPolicyState extends KioskPolicyFlags {
  readonly source: KioskPolicySource;
  readonly policyVersion?: number;
}

/**
 * The persistence instruction produced by one policy refresh.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * const action: KioskEmergencyCacheAction = { kind: "clear" };
 * ```
 */
export type KioskEmergencyCacheAction =
  | { readonly kind: "keep" }
  | { readonly kind: "clear" }
  | { readonly kind: "store"; readonly policyVersion: number };

/**
 * The pure result of reconciling server and cached policy evidence.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * declare const result: KioskPolicyRefreshResult;
 *
 * const source = result.state.source;
 * ```
 */
export interface KioskPolicyRefreshResult {
  readonly state: KioskPolicyState;
  readonly cacheAction: KioskEmergencyCacheAction;
}

/**
 * Initial fail-closed policy state used before cache and server reads finish.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * INITIAL_KIOSK_POLICY_STATE.localEmergencyAlertEnabled; // false
 * ```
 */
export const INITIAL_KIOSK_POLICY_STATE: KioskPolicyState = {
  source: "loading",
  ...DEFAULT_KIOSK_POLICY_FLAGS,
};

/**
 * Returns whether the projected policy authorizes the device-local alert.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * const available = isLocalEmergencyAvailable(INITIAL_KIOSK_POLICY_STATE);
 * ```
 */
export function isLocalEmergencyAvailable(
  state: KioskPolicyState,
): boolean {
  return (
    state.localEmergencyAlertEnabled &&
    (state.source === "current" || state.source === "offline-cache")
  );
}

function unavailableState(): KioskPolicyState {
  return {
    source: "unavailable",
    ...DEFAULT_KIOSK_POLICY_FLAGS,
  };
}

/**
 * Projects a valid local snapshot into emergency-only offline capability.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * const state = projectOfflineKioskPolicy(undefined);
 * ```
 */
export function projectOfflineKioskPolicy(
  snapshot: KioskEmergencyCacheSnapshot | undefined,
): KioskPolicyState {
  if (snapshot === undefined) return unavailableState();

  return {
    source: "offline-cache",
    patientCreationEnabled: false,
    manualPrescriptionFallbackEnabled: false,
    localEmergencyAlertEnabled: true,
    policyVersion: snapshot.policyVersion,
  };
}

/**
 * Reconciles one decoded RPC outcome with the last valid local snapshot.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * const result = resolveKioskPolicyRefresh(
 *   { kind: "transport-failure" },
 *   undefined,
 * );
 * ```
 */
export function resolveKioskPolicyRefresh(
  outcome: KioskPolicyReadOutcome,
  cachedSnapshot: KioskEmergencyCacheSnapshot | undefined,
): KioskPolicyRefreshResult {
  if (outcome.kind === "transport-failure") {
    return {
      state: projectOfflineKioskPolicy(cachedSnapshot),
      cacheAction: { kind: "keep" },
    };
  }

  if (outcome.kind === "malformed") {
    return {
      state: unavailableState(),
      cacheAction: { kind: "clear" },
    };
  }

  const { view } = outcome;
  if (view.availability === "unavailable") {
    return {
      state: unavailableState(),
      cacheAction: { kind: "clear" },
    };
  }

  return {
    state: {
      source: "current",
      ...view.flags,
      policyVersion: view.version,
    },
    cacheAction: view.flags.localEmergencyAlertEnabled
      ? { kind: "store", policyVersion: view.version }
      : { kind: "clear" },
  };
}

/**
 * Constructs a decoded current-policy outcome for composition and tests.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * declare const view: KioskPolicyView;
 *
 * const outcome = currentKioskPolicy(view);
 * ```
 */
export function currentKioskPolicy(
  view: KioskPolicyView,
): KioskPolicyReadOutcome {
  return { kind: "current", view };
}
